import { useEffect, useState, useMemo, useCallback, } from 'react';
import {
  Table,
  Button,
  Select,
  DatePicker,
  Tag,
  Empty,
  Spin,
  Input,
  Tooltip,
  message,
  notification,
  Popconfirm,
} from 'antd';
import { format } from 'date-fns';
import {
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { downloadExcel } from '../../utils/downloadExcel';

import useDebounce from '../../utils/debounce';
import {
  useFetchChargerHistory,
  useGetPaymentInfoFromTranscation,
} from './Hooks';
import { useGenerateCustomReport } from '../report/Hook/Index';
import TransactionDetailsModal from '../../components/Modals/TransactionDetailsModal';
import { useOperatorStore } from '../../store/useOperatorStore';
import {
  DebouncedSelect,
  fetchChargerList,
  fetchCustomerList,
  fetchLocationList,
} from '../../components/useDebouncedSelectLocation';
import { MdRefresh } from 'react-icons/md';
import { unixToLocalDate } from '../../utils/CommonFunctions';
import PaymentDetailsModal from './PaymentDetailsModal'; // Import the new modal component
import SingleChargerDrawer from '../locations/[id]/SingleChargerDrawer';
import CustomerDrawerView from '../../components/CustomerDrawerView';

const STATUS_OPTIONS = [
  'Completed',
  'Failed',
  'InProgress',
  'Interrupted',
];
const STATUS_COLORS = {
  PENDING: 'gold',
  INITIATED: 'cyan',
  INPROGRESS: 'geekblue',
  BLOCKED: 'red',
  EXPIRED: 'orange',
  INVALID: 'volcano',
  CONCURRENT_TX: 'purple',
  COMPLETED: 'green',
  FAILED: 'volcano',
  INTERRUPTED: 'cyan',
};

const TYPE_COLORS = {
  CREDIT: 'lime',
  DEBIT: 'red',
  REFUND: 'blue',
  OUTSTANDING: 'orange',
};

const STATUS_ICONS = {
  PENDING: <ClockCircleOutlined />,
  INITIATED: <SyncOutlined spin />,
  INPROGRESS: <SyncOutlined />,
  BLOCKED: <CloseCircleOutlined />,
  EXPIRED: <ExclamationCircleOutlined />,
  INVALID: <CloseCircleOutlined />,
  CONCURRENT_TX: <SyncOutlined spin />,
  COMPLETED: <CheckCircleOutlined />,
  FAILED: <CloseCircleOutlined />,
  INTERRUPTED: <ExclamationCircleOutlined />,
};

const TYPE_ICONS = {
  CREDITED: <CheckCircleOutlined />,
  DEBITED: <CloseCircleOutlined />,
  REFUNDED: <ExclamationCircleOutlined />,
  OUTSTANDING: <ClockCircleOutlined />,
};

const PaymentInfoTable = ({ transactionId }) => {
  const { t } = useTranslation('chargingHistoryPage');
  const { data: paymentInfo, isLoading } = useGetPaymentInfoFromTranscation({
    id: transactionId,
  });

  const [selectedPayment, setSelectedPayment] = useState(null);

  if (isLoading) {
    return <div className='flex justify-center items-center'><Spin /></div>;
  }

  if (!paymentInfo || paymentInfo.length === 0) {
    return <Empty description={t('NoTransactionsFound')} />;
  }

  return (
    <>
      <div>
        <Table
          columns={[
            {
              title: t('DateColumn'),
              dataIndex: 'created_at',
              key: 'created_at',
              align: 'center',
              render: (value) => unixToLocalDate(value),
              ellipsis: true,
            },
            {
              title: t('Type'),
              dataIndex: 'type',
              key: 'type',
              align: 'center',
              render: (type) =>
                type === 'REFUND' ? (
                  <Tooltip title={t('RefundTooltip')}>
                    <Tag color={TYPE_COLORS[type.toUpperCase()] || 'default'} variant="outlined">
                      {TYPE_ICONS[type]} {t(type)}
                    </Tag>
                  </Tooltip>
                ) : (
                  <Tag color={TYPE_COLORS[type] || 'default'} variant="outlined">
                    {TYPE_ICONS[type]} {t(type)}
                  </Tag>
                ),
              responsive: ['md'],
            },
            {
              title: t('Source'),
              dataIndex: 'source',
              key: 'source',
              align: 'center',
              responsive: ['lg'],
              ellipsis: true,
            },
            {
              title: t('Status'),
              dataIndex: 'status',
              key: 'status',
              align: 'center',
              render: (status) => (
                <Tag className='!capitalize' color={STATUS_COLORS[status?.toUpperCase()] || 'default'} variant="outlined">
                  <div className='flex items-center gap-1 capitalize'>
                    {STATUS_ICONS[status?.toUpperCase()]} {t(status, { defaultValue: status })}
                  </div>
                </Tag>
              ),
            },
            {
              title: t('Actions'),
              key: 'actions',
              align: 'center',
              render: (_, record) => (
                <Tooltip title={t('ViewDetails')}>
                  <Button
                    icon={<EyeOutlined />}
                    type="default"
                    onClick={() => setSelectedPayment(record)}
                  />
                </Tooltip>
              ),
            },
          ]}
          dataSource={paymentInfo}
          pagination={false}
          rowKey={(item) => item.id}
          scroll={{ x: 'max-content' }}
        />
      </div>
      {selectedPayment && (
        <PaymentDetailsModal
          payment={selectedPayment}
          onClose={() => setSelectedPayment(null)}
        />
      )}
    </>
  );
};
PaymentInfoTable.propTypes = {
  transactionId: PropTypes.string,
};

const ChargingHistoryTable = () => {
  const { t } = useTranslation(['chargingHistoryPage', 'common']);
  const location = useLocation();
  const navigate = useNavigate();
  const charger = location.state?.charger;
  const { t: tCommon } = useTranslation('common');
  const { operatorId: operatorIdFromStore } = useOperatorStore();
  const operatorId = operatorIdFromStore || charger?.operator;
  const [searchParams] = useSearchParams();
  const [openChargerModal, setOpenChargerModal] = useState(null);
  const [searchType, setSearchType] = useState('CHARGING_HISTORY'); // Default search type
  const [searchQuery, setSearchQuery] = useState(null);
  const [status, setStatus] = useState();
  const [isRFID, setIsRFID] = useState(null); // New state for isRFID filter
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15); // New state for page size
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [dateRange, setDateRange] = useState([]);
  const [transactionId, setTransactionId] = useState(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [openCustomerModal, setOpenCustomerModal] = useState(null);
  const { mutate: generateReport, isPending: isExporting } = useGenerateCustomReport();
  const [isClientSideExporting, setIsClientSideExporting] = useState(false);

  const pagination = useMemo(
    () => ({
      limit: pageSize,
      offset: page,
      status,
      searchType,
      searchValue: debouncedSearchQuery && typeof debouncedSearchQuery === 'object' ? debouncedSearchQuery.value : debouncedSearchQuery,
      startDate: dateRange?.[0]
        ? format(new Date(dateRange[0]), 'yyyy-MM-dd')
        : '',
      endDate: dateRange?.[1]
        ? format(new Date(dateRange[1]), 'yyyy-MM-dd')
        : '',
      isRFID,
    }),
    [debouncedSearchQuery, status, searchType, dateRange, page, isRFID, pageSize]
  );
  const {
    data: transactions = { paginatedData: [], totalCount: 0 },
    isLoading,
    isFetching,
    refetch,
  } = useFetchChargerHistory(pagination, operatorId);

  const handleClearFilters = useCallback(() => {
    setSearchQuery(null);
    setStatus(undefined);
    setIsRFID(null); // Reset isRFID filter
    setDateRange([]);
    setPage(1); // Reset page
    setPageSize(10); // Reset page size
    setFiltersApplied(false);
    delete location.state.charger;
  }, [location]);

  useEffect(() => {
    if (charger) {
      setSearchType('CHARGER');
      setSearchQuery({
        label: charger.name || charger.id,
        value: charger.id
      });
    }
  }, [charger]);

  useEffect(() => {
    setFiltersApplied(
      !!((searchQuery && typeof searchQuery === 'object' ? searchQuery.value : searchQuery) || status || dateRange?.length || isRFID !== null)
    );
    setPage(1); // Reset page when filters change
  }, [searchQuery, status, dateRange, isRFID]);

  useEffect(() => {
    // On mount, check for status in query params
    const urlStatus = searchParams.get('status');
    if (urlStatus) {
      setStatus(urlStatus);
      setFiltersApplied(true);
    }
    // eslint-disable-next-line
  }, []);

  const searchOptions = useMemo(
    () => [
      { label: t('searchByLocation'), value: 'LOCATION' },
      { label: t('searchByCharger'), value: 'CHARGER' },
      { label: t('searchByCustomer'), value: 'CUSTOMER' },
      { label: t('searchByTransactionID'), value: 'CHARGING_HISTORY' },
      { label: t('searchByRFID'), value: 'RFID' },
    ],
    [t]
  );

  // Helper for User rendering (line 411-435)
  const renderUser = (customerData, data) => {
    if (data?.testCharging) {
      return t('testCharging');
    }
    if ((!customerData || customerData.length === 0) && data.isRFID) {
      return `RFID | ${data.RFID || '—'}`;
    }
    if (!customerData || customerData.length === 0) return 'Unknown';
    const customer = customerData[0];
    const name = `${customer.fname || ''} ${customer.lname || ''}`.trim();
    const rfidPart = data?.isRFID && data?.RFID ? ` RFID: ${data.RFID}` : '';
    const customerId =
      customer?._id ?? customer?.id ?? data?.customerId;
    const rowOperatorId = data?.operatorId ?? operatorId;
    return (
      <Tooltip title={customer.phoneNumber}>
        <div
          className="cursor-pointer text-primary flex flex-col"
          onClick={() => {
            if (!customerId || !rowOperatorId) return;
            setOpenCustomerModal({
              id: customerId,
              operatorId: rowOperatorId,
            });
          }}
        >
          <span>{name}</span>
          <span className="text-xs text-gray-500">{rfidPart}</span>
        </div>
      </Tooltip>
    );
  };

  // Helper for search input rendering (lines 346-348, 428-430)
  const renderSearchInput = () => {
    if (searchType === 'RFID') {
      return (
        <Input
          placeholder={t('enterRFID')}
          value={searchQuery && typeof searchQuery === 'object' ? searchQuery.value : searchQuery || ''}
          allowClear
          className="w-full md:!max-w-[250px]"
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      );
    }
    if (['TRANSACTION', 'CHARGING_HISTORY'].includes(searchType)) {
      return (
        <div className='w-full md:!max-w-[200px]'>
          <Input
            placeholder={t('enterID')}
            value={searchQuery && typeof searchQuery === 'object' ? searchQuery.value : searchQuery || ''}
            allowClear
            className="w-full md:!max-w-[200px]"
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      );
    }
    // Extracted nested ternary into independent statement
    let fetchOptions;
    if (searchType === 'LOCATION') {
      fetchOptions = (search) => fetchLocationList({ operatorId, search });
    } else if (searchType === 'CHARGER') {
      fetchOptions = (search) => fetchChargerList({ operatorId, search });
    } else {
      fetchOptions = (search) => fetchCustomerList({ operatorId, search });
    }

    return (
      <DebouncedSelect
        value={searchQuery}
        onChange={setSearchQuery}
        operatorId={operatorId}
        allowClear
        fetchOptions={fetchOptions}
        placeholder={t('typeForSearch')}
        className="w-full md:!max-w-[200px]"
        labelInValue={searchType === 'CHARGER'}
      />
    );
  };

  const columns = useMemo(() => {
    return [
      {
        title: t('DateColumn'),
        dataIndex: 'created_at',
        key: 'created_at',
        ellipsis: true,
        align: 'center',
        render: (value) => {
          Date(value);
          if (!value) return '-';
          return (
            <div className="whitespace-nowrap"> {unixToLocalDate(value)}</div>
          );
        },
      },
      {
        title: t('TransactionID'),
        dataIndex: 'ocppTransactionId',
        key: 'transactionId',
        ellipsis: true,
        align: 'center',
        render: (value, record) => (
          <span
            className="cursor-pointer text-primary"
            onClick={() => setTransactionId(record._id)}
          >
            {value || 'N/A'}
          </span>
        ),
      },
      {
        title: t('ChargerName'),
        dataIndex: 'chargerData',
        key: 'chargerName',
        ellipsis: true,
        tooltip: true,
        align: 'center',
        render: (chargerData) => (
          <span
            className="cursor-pointer text-primary"
            onClick={() =>
              setOpenChargerModal({
                id: chargerData?.[0]?._id,
                operatorId: chargerData?.[0]?.operatorId,
              })
            }
          >
            {chargerData?.[0]?.name || 'N/A'}
          </span>
        ),
        responsive: ['md'],
      },
      {
        title: t('User'),
        dataIndex: 'customerData',
        key: 'customer',
        ellipsis: true,
        tooltip: true,
        align: 'center',
        render: (customerData, data) => renderUser(customerData, data),
        responsive: ['sm'],
      },
      {
        title: t('Position'),
        key: 'position',
        align: 'center',
        ellipsis: true,
        render: (_, record) => record.customerData?.[0]?.position || '—',
        responsive: ['md'],
      },

      {
        title: t('ConsumedEnergy'),
        key: 'consumedEnergy',
        ellipsis: true,
        align: 'center',
        render: (record) => (
          <span>{Math.max(0, (record.consumption / 1000).toFixed(4))}</span>
        ),
        responsive: ['md'],
      },
      {
        title: t('Status'),
        dataIndex: 'status',
        key: 'status',
        align: 'center',
        render: (status) => (
          <Tag color={STATUS_COLORS[status?.toUpperCase()] || 'default'} variant="outlined">
            {STATUS_ICONS[status?.toUpperCase()]} {t(status)}
          </Tag>
        ),
      },
      {
        title: t('Actions'),
        key: 'actions',
        align: 'center',
        render: (_, record) => (
          <Tooltip title={t('ViewDetails')}>
            <Button
              icon={<EyeOutlined />}
              type="default"
              onClick={() => setTransactionId(record._id)}
            />
          </Tooltip>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, operatorId]);

  const datePresets = [
    { label: tCommon('today'), value: [dayjs(), dayjs()] },
    {
      label: tCommon('yesterday'),
      value: [dayjs().add(-1, 'd'), dayjs().add(-1, 'd')],
    },
    { label: tCommon('last7Days'), value: [dayjs().add(-7, 'd'), dayjs()] },
    { label: tCommon('last30Days'), value: [dayjs().add(-30, 'd'), dayjs()] },
    {
      label: tCommon('thisMonth'),
      value: [dayjs().startOf('month'), dayjs().endOf('month')],
    },
    {
      label: tCommon('lastMonth'),
      value: [
        dayjs().add(-1, 'month').startOf('month'),
        dayjs().add(-1, 'month').endOf('month'),
      ],
    },
  ];

  // Function to check if date range is more than 3 months
  const isDateRangeMoreThan3Months = (startDate, endDate) => {
    if (!startDate || !endDate) return false;
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const monthsDiff = end.diff(start, 'month', true);
    return monthsDiff > 3;
  };

  // Function to proceed with report generation
  const proceedWithReportGeneration = () => {
    const payload = {
      reportTemplate: "CHARGING_SESSION",
      startTime: pagination.startDate,
      endTime: pagination.endDate,
      ...(operatorId && { operatorId }),
      ...(status && { status }),
      ...(searchType === 'CHARGER' && searchQuery?.value && { chargerId: searchQuery.value }),
      ...(searchType === 'LOCATION' && searchQuery?.value && { locationId: searchQuery.value }),
      reportName: `Charging_Session_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}`,
      ...(isRFID !== null && { isRFID }),
    };

    generateReport(payload, {
      onSuccess: () => {
        notification.success({
          message: t('reportGenerationStarted'),
          description: t('reportAvailableInCustomReports'),
          duration: 6,
          btn: (
            <Button
              type="primary"
              size="small"
              onClick={() => {
                navigate('/dashboard/reports', {
                  state: { activeMainTab: 'customReports' },
                });
                notification.destroy();
              }}
            >
              {t('viewCustomReports')}
            </Button>
          ),
        });
      },
      onError: (error) => {
        console.error('Export failed:', error);
        // Extract backend error message
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.status_message ||
          error?.response?.data?.error ||
          error?.message ||
          t('reportGenerationFailed');
        message.error(errorMessage);
      }
    });
  };

  // Function to handle export logic (without confirmation)
  const executeExport = async () => {
    if (!transactions?.paginatedData?.length) {
      message.warning(t('noDataToExport'));
      return;
    }

    // Check if date range is selected - if yes, always generate custom report
    const hasDateRange = dateRange &&
      Array.isArray(dateRange) &&
      dateRange.length === 2 &&
      dateRange[0] &&
      dateRange[1] &&
      pagination.startDate &&
      pagination.endDate;

    if (hasDateRange) {
      // Generate custom report when date filters are applied
      proceedWithReportGeneration();
    } else {
      // Fallback to client-side Excel export when no date range is selected
      setIsClientSideExporting(true);
      try {
        const dataToExport = transactions.paginatedData;

        const csvData = dataToExport.map((record) => ({
          Date: record.created_at ? unixToLocalDate(record.created_at) : 'N/A',
          'Transaction ID': record.ocppTransactionId || 'N/A',
          'Charger Name': record.chargerData?.[0]?.name || 'N/A',
          'User': record.customerData?.[0]
            ? `${record.customerData[0].fname || ''} ${record.customerData[0].lname || ''}`.trim() + (record.isRFID && record.RFID ? ` | RFID: ${record.RFID}` : '') + ` - ${record.customerData[0].phoneNumber || ''}`
            : (record.isRFID ? `RFID | ${record.RFID || '—'}` : 'Unknown'),
          'Position': record.customerData?.[0]?.position || '',
          'Consumed Energy (kWh)': Math.max(0, (record.consumption / 1000).toFixed(4)),
          'Status': record.status ? t(record.status, { defaultValue: record.status }) : 'N/A',
        }));

        await downloadExcel(
          csvData,
          `charging_history_${new Date().toISOString().split('T')[0]}.xlsx`,
        );
        message.success(tCommon('exportSuccessCount', { count: dataToExport.length }));
      } catch (error) {
        console.error('Export failed:', error);
        message.error(tCommon('exportFailed'));
      } finally {
        setIsClientSideExporting(false);
      }
    }
  };

  // Confirm whenever a date range is set: export then runs as a server-side report
  const shouldShowPopconfirm = useMemo(() => {
    const hasDateRange = dateRange &&
      Array.isArray(dateRange) &&
      dateRange.length === 2 &&
      dateRange[0] &&
      dateRange[1] &&
      pagination.startDate &&
      pagination.endDate;

    if (!hasDateRange) return false;

    return true;
  }, [dateRange, pagination.startDate, pagination.endDate]);

  // Export button component
  const ExportButton = () => {
    const isLongRange =
      shouldShowPopconfirm && isDateRangeMoreThan3Months(dateRange[0], dateRange[1]);
    const isDisabled = !transactions?.paginatedData?.length;

    const buttonProps = {
      icon: <DownloadOutlined />,
      type: 'default',
      loading: isExporting || isClientSideExporting,
      disabled: isDisabled,
      className: 'w-full',
    };

    if (shouldShowPopconfirm && !isDisabled) {
      return (
        <Popconfirm
          title={isLongRange ? t('longPeriodWarningTitle') : t('generateReportConfirmTitle')}
          description={
            isLongRange
              ? t('longPeriodWarningMessage')
              : t('generateReportConfirmMessage', {
                  from: dayjs(dateRange[0]).format('DD MMM YYYY'),
                  to: dayjs(dateRange[1]).format('DD MMM YYYY'),
                })
          }
          okText={t('generateConfirmOk')}
          cancelText={t('cancel') || 'Cancel'}
          onConfirm={executeExport}
          icon={<ExclamationCircleOutlined style={{ color: isLongRange ? '#faad14' : undefined }} />}
        >
          <Button {...buttonProps} />
        </Popconfirm>
      );
    }

    return (
      <Button
        {...buttonProps}
        onClick={executeExport}
      />
    );
  };

  return (
    <div className="space-y-3 h-full">

      <div className="flex items-center gap-2 p-1 w-full overflow-x-auto hide-scrollbar"><Select
        value={searchType}
        onChange={setSearchType}
        className="w-full md:!max-w-[200px]"
        options={searchOptions}
      />
        {renderSearchInput()}
        <Select
          allowClear
          placeholder={t('selectStatus')}
          className="w-full md:!max-w-[200px]"
          value={status}
          onChange={(value) => {
            setStatus(value);
            setPage(1); // Reset page
            // want to remove from state.location.state.charger
            delete location.state.charger;
          }}
          options={STATUS_OPTIONS.map((status) => ({
            label: t(status),
            value: status,
          }))}
        />
        <Select
          allowClear
          placeholder={t('filterByRFID')}
          className="w-full md:!max-w-[200px]"
          value={isRFID}
          onChange={setIsRFID}
          options={[
            { label: t('Yes'), value: true },
            { label: t('No'), value: false },
          ]}
        />
        <DatePicker.RangePicker
          value={dateRange}
          presets={datePresets}
          placeholder={t('filterByDate')}
          className="w-full md:!max-w-[250px]"
          onChange={(dates) => {
            setDateRange(dates);
            setPage(1); // Reset page
          }}
        />
        {filtersApplied && (
          <Button
            onClick={handleClearFilters}
          >
            {t('clear')}
          </Button>
        )}
        <div>
        <Button
          icon={<MdRefresh />}
          loading={isFetching}
          type="default"
          onClick={refetch}
          className="w-full"
        />
        </div>
        <div>
        <Tooltip title={t('exportCSV')}>
          <ExportButton />
        </Tooltip>
        </div>
      </div>
      <div className='w-full'>
        <Table
          columns={columns}
          dataSource={transactions?.paginatedData || []}
          loading={isLoading || isFetching}
          size="middle"
          scroll={{ x: 1400, y: '67vh' }}
          pagination={{
            current: page,
            showSizeChanger: true,
            total: transactions?.totalCount || 0,
            pageSize: pageSize,
            pageSizeOptions: ['10', '20', '50', '100', '200'],
            onChange: (page, size) => {
              setPage(page);
              setPageSize(size);
            },
            onShowSizeChange: (current, size) => {
              setPage(1);
              setPageSize(size);
            },
            locale: {
              items_per_page: ` / ${t('page')}`,
            },
            size: 'default',
            position: ['bottomCenter'],
          }}
          variant="outlined"
          rowKey={(record) => record._id}
        />
      </div>
      {transactionId && (
        <TransactionDetailsModal
          id={transactionId}
          open={Boolean(transactionId)}
          onClose={() => setTransactionId(null)}
        />
      )}
      {openChargerModal && (
        <SingleChargerDrawer
          selectedCharger={openChargerModal?.id || ''}
          isOpen={Boolean(openChargerModal)}
          onClose={() => setOpenChargerModal(null)}
          operatorId={openChargerModal?.operatorId || ''}
        />
      )}
      {openCustomerModal?.id && (
        <CustomerDrawerView
          id={openCustomerModal.id}
          operatorId={openCustomerModal.operatorId}
          open={Boolean(openCustomerModal?.id)}
          onClose={() => setOpenCustomerModal(null)}
        />
      )}
    </div>
  );
};

export default ChargingHistoryTable;
